import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormHelperText,
  IconButton,
  MenuItem,
  MenuListProps,
  Select,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import { Box } from '@mui/system';

import { cn } from 'components/lib/utils';
import Typography from 'components/Typography/Typography';
import UploadImage from 'components/UploadImage/UploadImage';

import usePermissions from '../../api/usePermissions';
import { EAssetType } from '../../constants/enums';
import { UploadTooltipsDescription } from '../../hooks/useUpload';
import { truncateText } from '../../utils/formattingUtil';

import {
  AcSelectImageItem,
  AcSelectItem,
  AcSelectProps,
  EAcSelectItemRenderType,
  EAcSelectOptions
} from './AcSelect.types';
import useAcSelect from './useAcSelect';

import './style.scss';

const AcSelect = ({
  items = [],
  className = '',
  buttonDataTestId,
  listDataTestId,
  optionDataTestIdPrefix,
  isRepeatSelect,
  renderValue,
  header = '',
  helperText = '',
  helperTextColor = '',
  tooltip = '',
  uploadConfig = undefined,
  required = false,
  headerSize = '14',
  isCheckBox = false,
  isLimited,
  setSelectedItems = () => {},
  loading = false,
  defaultCheckedItems,
  isSelectAllEnabled = false,
  isApplyBtnEnabled = true,
  multipleSelect = false,
  isSearchEnabled = false,
  searchPlaceholder = 'Search',
  allowSearchByContent = false,
  renderType,
  disabled,
  onClear,
  dataTestValidationId = '',
  dataTestListId = '',
  dataTestBoxId = '',
  dataTestHeaderId = '',
  selectAllCustomLabel,
  placeholder = '',
  isPlaceholderBold = false,
  renderStatusTextOverride,
  selectFieldClassName,
  IconComponent,
  ...props
}: AcSelectProps) => {
  const { imageRenderFunction } = useAcSelect();
  const { canUserEdit } = usePermissions();
  const [checkedItems, setCheckedItems] = useState<string[]>(
    defaultCheckedItems ?? []
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    defaultCheckedItems && multipleSelect
      ? setCheckedItems(defaultCheckedItems)
      : setCheckedItems(props.value);
  }, [props.value, defaultCheckedItems]);

  const uploadAsset = async (url: string, name: string, mimeType: string) => {
    uploadConfig!.onUploadSuccess?.(url, name, mimeType);
  };

  const handleCheckboxClick = (checkedItem: any, key?: string | number) => {
    const itemValue =
      items.find((item) => item.key === checkedItem)?.value || checkedItem;

    if (isSelectAllEnabled && key === EAcSelectOptions.ALL) {
      let newSelectedValues: any = items.map((item) => item.value);
      if (checkedItems.length === newSelectedValues.length) {
        newSelectedValues = [];
      }
      setCheckedItems(newSelectedValues);
      if (!isApplyBtnEnabled) {
        setSelectedItems(newSelectedValues);
      }
      return;
    }

    let checkedItemsArray = Array.isArray(checkedItems)
      ? checkedItems
      : [checkedItems];

    let newCheckedItems;
    const itemToCheck = multipleSelect ? itemValue : checkedItem;

    newCheckedItems = checkedItemsArray?.includes(itemToCheck)
      ? checkedItemsArray.filter((item) => item !== itemToCheck)
      : isLimited
        ? [itemToCheck]
        : [...checkedItemsArray, itemToCheck];

    // Remove undefined values from newCheckedItems
    newCheckedItems = newCheckedItems.filter((item) => item !== undefined);
    setCheckedItems(newCheckedItems);

    if (!isApplyBtnEnabled) {
      setSelectedItems(newCheckedItems);
    }
  };

  const handleIsItemChecked = (filterBy: any) => {
    if (isSelectAllEnabled && filterBy === EAcSelectOptions.ALL) {
      return checkedItems.length === items.length;
    }
    return checkedItems?.includes(filterBy);
  };

  const getItemContentByType = (item: AcSelectItem | AcSelectImageItem) => {
    const { renderFunction, ...itemProps } = item ?? {};

    if (renderType === EAcSelectItemRenderType.IMAGE) {
      return imageRenderFunction(item, props.imgHeight);
    }

    return renderFunction ? renderFunction(itemProps) : itemProps.content;
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (!item.value) return false;

      const existByContent =
        allowSearchByContent &&
        item.content &&
        item.content
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const existByValue =
        item.value &&
        item.value.toString().toLowerCase().includes(searchQuery.toLowerCase());

      return existByContent || existByValue;
    });
  }, [items, searchQuery, allowSearchByContent]);

  const renderSelectItems = useCallback(() => {
    if (!Array.isArray(items)) {
      return [];
    }

    const selectItems = isSearchEnabled ? filteredItems : items;

    return selectItems?.map((item, index) => {
      const { renderFunction, ...itemProps } = item ?? {};
      const filterBy = itemProps.filterBy || itemProps.value;

      return (
        <MenuItem
          {...itemProps}
          key={itemProps.key || index}
          data-testid={
            optionDataTestIdPrefix && itemProps.value
              ? `${optionDataTestIdPrefix}-${itemProps.value}`
              : undefined
          }
          sx={{
            padding: isCheckBox ? '8px 12px' : '6px 16px',
            fontSize: isCheckBox ? '14px' : '16px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCheckboxClick(filterBy, itemProps?.key);
          }}
        >
          {isCheckBox && (
            <Checkbox
              checked={
                checkedItems.length === items.length && multipleSelect
                  ? handleIsItemChecked(EAcSelectOptions.ALL)
                  : handleIsItemChecked(filterBy)
              }
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxClick(filterBy, itemProps?.key);
              }}
              size="small"
              sx={{
                padding: 0,
                marginRight: '6px'
              }}
            />
          )}
          <Box component="span" sx={{ flexGrow: 1 }}>
            {getItemContentByType(item)}
          </Box>
        </MenuItem>
      );
    });
  }, [items, checkedItems, searchQuery]);

  const handleRenderValue = (selected: string | string[]) => {
    if (renderStatusTextOverride) return renderStatusTextOverride(selected);
    if (renderValue) return renderValue(selected);
    if (Array.isArray(selected)) {
      return truncateText(selected.join(', '));
    }
    const currentItem = items.find((item) => item.value === selected);
    const content = currentItem ? getItemContentByType(currentItem) : selected;
    return typeof content === 'string' ? truncateText(content) : content;
  };

  const handleClose = () => {
    setIsOpen(false);
    if (searchQuery.length > 0) {
      setSearchQuery('');
    }
  };

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (props?.onOpen) props.onOpen();
  }, [props?.onOpen]);

  return (
    <Stack
      className={`select-container formContent-select ${className}`}
      direction="column"
    >
      {(header || loading) && (
        <Stack
          className="select-content"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          position="relative"
          marginBottom={'4px'}
          gap={1}
        >
          <Stack
            className="select-headerBlock formContent-select-fieldTitle"
            direction="row"
            alignItems="center"
            gap={1}
          >
            {header && (
              <Tooltip arrow title={tooltip} placement="top">
                <h3 data-testid={dataTestHeaderId}>
                  {required && (
                    <span
                      style={{
                        color: 'red',
                        marginRight: '6px'
                      }}
                    >
                      *
                    </span>
                  )}
                  {header}
                </h3>
              </Tooltip>
            )}
            {loading && <CircularProgress size={12} />}
          </Stack>
        </Stack>
      )}
      <Stack className="select-block">
        <>
          {onClear && props?.value && (
            <div className="select-block__close-btn">
              <IconButton onClick={onClear} size="small">
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>
          )}
          <Select
            displayEmpty
            {...props}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            disabled={disabled || loading}
            IconComponent={IconComponent}
            sx={{
              '.MuiTypography-root': {
                overflowX: 'clip'
              },
              ...props.sx
            }}
            multiple={isCheckBox}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: '280px',
                  width: 'auto',
                  display: 'flex',
                  marginTop: '5px',
                  flexDirection: 'column'
                }
              },
              MenuListProps: {
                style: {
                  paddingTop: isCheckBox ? '9px' : '8px',
                  paddingBottom: isCheckBox ? 0 : '8px',
                  flexGrow: 1,
                  overflowY: 'auto'
                },
                'data-testid': listDataTestId || dataTestListId
              } as Partial<MenuListProps>,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            className={cn(
              'formContent-select-selectField',
              selectFieldClassName
            )}
            renderValue={(selected) => {
              if (
                !selected ||
                (Array.isArray(selected) && selected?.length === 0)
              ) {
                return (
                  <div
                    className={cn(
                      ' text-p-s',
                      !isPlaceholderBold && 'text-neutral-400'
                    )}
                  >
                    {placeholder}
                  </div>
                );
              }
              const renderedValue = handleRenderValue(
                selected as string | string[]
              );
              return (
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                >
                  {renderedValue}
                </div>
              );
            }}
            SelectDisplayProps={
              {
                'data-testid': buttonDataTestId || dataTestBoxId
              } as React.ComponentProps<'div'>
            }
          >
            {isSearchEnabled && (
              <div className="formContent-select-searchField">
                <TextField
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder={searchPlaceholder}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  size="small"
                  fullWidth
                  autoFocus
                />
              </div>
            )}

            {isCheckBox ? (
              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {isSelectAllEnabled && filteredItems.length > 0 && (
                  <MenuItem
                    key={EAcSelectOptions.ALL}
                    sx={{
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckboxClick(
                        EAcSelectOptions.ALL,
                        EAcSelectOptions.ALL
                      );
                    }}
                  >
                    <Checkbox
                      checked={handleIsItemChecked(EAcSelectOptions.ALL)}
                      size="small"
                      sx={{
                        padding: 0,
                        marginRight: '6px'
                      }}
                    />
                    {selectAllCustomLabel || 'Select all'}
                  </MenuItem>
                )}
                {renderSelectItems()}
                {isSearchEnabled &&
                  searchQuery &&
                  filteredItems.length === 0 && (
                    <Typography
                      tag="p"
                      className="text-center m-4 text-p-s text-base-mutedForeground"
                    >
                      No results found
                    </Typography>
                  )}
              </Box>
            ) : (
              [
                ...renderSelectItems(),
                isSearchEnabled &&
                  searchQuery &&
                  filteredItems.length === 0 && (
                    <Box
                      key="no-results"
                      sx={{
                        padding: '12px 16px',
                        color: '#999',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}
                    >
                      No results found
                    </Box>
                  )
              ]
            )}
            {isCheckBox &&
              isApplyBtnEnabled &&
              !(
                isSearchEnabled &&
                searchQuery &&
                filteredItems.length === 0
              ) && (
                <div
                  className="apply-button--container"
                  style={{ padding: '6px 8px' }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    disabled={false}
                    onClick={() => {
                      setSelectedItems(checkedItems);
                      handleClose();
                    }}
                    sx={{
                      width: '100%'
                    }}
                    disableElevation={true}
                  >
                    Apply
                  </Button>
                </div>
              )}
          </Select>
        </>
      </Stack>
      {helperText && (
        <FormHelperText
          error={!helperTextColor}
          data-testid={dataTestValidationId}
          color={helperTextColor}
        >
          {helperText}
        </FormHelperText>
      )}
      {uploadConfig && (
        <Stack direction="row" alignItems="center">
          {uploadConfig.uploadMessage ? (
            <Tooltip
              arrow
              title={`${uploadConfig!.uploadType !== EAssetType.ANIMATION && uploadConfig!.uploadType !== EAssetType.BUTTON_ASSET_SUFFIX ? UploadTooltipsDescription : ''} ${uploadConfig.uploadMessage}`}
              placement="right"
              PopperProps={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -12]
                    }
                  }
                ]
              }}
            >
              <Button
                size="small"
                variant="text"
                disabled={disabled || !canUserEdit}
                disableRipple
                onClick={() => setIsImageModalOpen(true)}
                className="select-uploadBtn formContent-select-uploadButton"
                data-testid={props?.uploadBtnTestId}
              >
                Upload
              </Button>
            </Tooltip>
          ) : (
            <Button
              size="small"
              variant="text"
              disabled={disabled || !canUserEdit}
              disableRipple
              onClick={() => setIsImageModalOpen(true)}
              className="select-uploadBtn formContent-select-uploadButton"
              data-testid={props?.uploadBtnTestId}
            >
              Upload
            </Button>
          )}
        </Stack>
      )}
      {isImageModalOpen && (
        <UploadImage
          className="w-full"
          title=""
          description=""
          buttonText=""
          selectedImageUrl={''}
          onUpload={(url, name, mimeType) => {
            uploadAsset(url, name, mimeType);
            setIsImageModalOpen(false);
          }}
          modalOnly
          uploadOnly
          isModalOpen={isImageModalOpen}
          externalModalOpenFunction={setIsImageModalOpen}
          forceType={uploadConfig?.uploadType}
        />
      )}
    </Stack>
  );
};

export default AcSelect;
